/**
 * Content Manager - Content Store
 *
 * Zustand store for managing seeds, outputs, groups, and notes.
 * Connected to AWS API via service layer.
 */

import { create } from 'zustand';
import { seedsService, outputsService, groupsService, notesService, eventsService, elevateService } from '../services/content';
import { usersService } from '../services/users';
import { notificationsService } from '../services/notifications';
import { teamsNotificationHelper } from '../services/integrations';
import { useUIStore } from './uiStore';
import { useAuthStore } from './authStore';
import { eventsClient, type ContentChangeEvent } from '../lib/awsConfig';
import { logger } from '../lib/logger';
import type {
  ContentSeed,
  ContentOutput,
  ContentGroup,
  ContentNote,
  ContentEvent,
  ContentStatus,
  SeedFormData,
  OutputFormData,
  NoteFormData,
  GroupFormData,
  EventFormData,
  FilterConfig,
} from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface ContentState {
  // Data
  seeds: ContentSeed[];
  outputs: ContentOutput[];
  groups: ContentGroup[];
  notes: ContentNote[];
  events: ContentEvent[];

  // Loading/Error state
  loading: boolean;
  initialLoadComplete: boolean;
  error: string | null;

  // Filters
  filters: FilterConfig;

  // Actions - Data Fetching
  fetchAllData: () => Promise<void>;
  fetchSeeds: () => Promise<void>;
  fetchOutputs: () => Promise<void>;
  fetchGroups: () => Promise<void>;
  fetchEvents: () => Promise<void>;

  // Actions - Seeds
  setSeeds: (seeds: ContentSeed[]) => void;
  addSeed: (data: SeedFormData) => Promise<ContentSeed | null>;
  updateSeed: (id: string, updates: Partial<ContentSeed>) => Promise<boolean>;
  deleteSeed: (id: string) => Promise<boolean>;
  updateSeedStatus: (id: string, status: ContentStatus) => Promise<boolean>;
  reorderSeeds: (sourceIndex: number, destIndex: number, groupId?: string | null) => void;
  moveSeedToGroup: (seedId: string, targetGroupId: string | null, targetIndex: number) => void;

  // Actions - Outputs
  setOutputs: (outputs: ContentOutput[]) => void;
  addOutput: (data: OutputFormData) => Promise<ContentOutput | null>;
  updateOutput: (id: string, updates: Partial<ContentOutput>) => Promise<boolean>;
  deleteOutput: (id: string) => Promise<boolean>;
  updateOutputStatus: (id: string, status: ContentStatus) => Promise<boolean>;
  reorderOutputs: (seedId: string, sourceIndex: number, destIndex: number) => void;

  // Actions - Groups
  setGroups: (groups: ContentGroup[]) => void;
  addGroup: (data: GroupFormData) => Promise<ContentGroup | null>;
  updateGroup: (id: string, updates: Partial<ContentGroup>) => Promise<boolean>;
  deleteGroup: (id: string) => Promise<boolean>;
  toggleGroupCollapsed: (id: string) => void;
  reorderGroups: (sourceIndex: number, destIndex: number) => void;

  // Actions - Notes
  setNotes: (notes: ContentNote[]) => void;
  addNote: (data: NoteFormData) => Promise<ContentNote | null>;
  updateNote: (id: string, updates: Partial<ContentNote>) => Promise<boolean>;
  deleteNote: (id: string) => Promise<boolean>;
  toggleNoteComplete: (id: string) => Promise<boolean>;

  // Actions - Events
  setEvents: (events: ContentEvent[]) => void;
  addEvent: (data: EventFormData) => Promise<ContentEvent | null>;
  updateEvent: (id: string, updates: Partial<ContentEvent>) => Promise<boolean>;
  deleteEvent: (id: string) => Promise<boolean>;
  reorderEvents: (sourceIndex: number, destIndex: number) => void;

  // Actions - Filters
  setFilters: (filters: FilterConfig) => void;
  clearFilters: () => void;

  // Actions - Loading/Error
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Bulk actions
  bulkUpdateStatus: (ids: string[], type: 'seed' | 'output', status: ContentStatus) => Promise<boolean>;
  bulkDelete: (seedIds: string[], outputIds: string[]) => Promise<boolean>;

  // Real-time subscriptions
  subscribeToChanges: () => () => void;
  handleContentChange: (event: ContentChangeEvent) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

const showToast = (type: 'success' | 'error' | 'info', message: string) => {
  useUIStore.getState().addToast({ type, message });
};

/**
 * Convert email address to a proper display name.
 * e.g., "gregorm@highland-marketing.com" -> "Gregorm"
 * e.g., "gregor.mackenzie@example.com" -> "Gregor Mackenzie"
 */
const getDisplayName = (name: string): string => {
  if (!name) return 'Unknown';
  if (name.includes('@')) {
    const namePart = name.split('@')[0];
    return namePart
      .replace(/[._-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return name;
};

// =============================================================================
// REAL-TIME EVENT BATCHING
// =============================================================================

// Buffer to accumulate real-time events for batched processing
// This prevents browser freezes when many WebSocket events arrive rapidly
interface PendingChanges {
  seeds: {
    inserts: ContentSeed[];
    updates: Map<string, Partial<ContentSeed>>;
    deletes: Set<string>;
  };
  outputs: {
    inserts: ContentOutput[];
    updates: Map<string, Partial<ContentOutput>>;
    deletes: Set<string>;
    deletesBySeedId: Set<string>;
  };
  groups: {
    inserts: ContentGroup[];
    updates: Map<string, Partial<ContentGroup>>;
    deletes: Set<string>;
  };
  notes: {
    inserts: ContentNote[];
    updates: Map<string, Partial<ContentNote>>;
    deletes: Set<string>;
  };
  events: {
    inserts: ContentEvent[];
    updates: Map<string, Partial<ContentEvent>>;
    deletes: Set<string>;
  };
}

const createEmptyPendingChanges = (): PendingChanges => ({
  seeds: { inserts: [], updates: new Map(), deletes: new Set() },
  outputs: { inserts: [], updates: new Map(), deletes: new Set(), deletesBySeedId: new Set() },
  groups: { inserts: [], updates: new Map(), deletes: new Set() },
  notes: { inserts: [], updates: new Map(), deletes: new Set() },
  events: { inserts: [], updates: new Map(), deletes: new Set() },
});

let pendingChanges: PendingChanges = createEmptyPendingChanges();
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY_MS = 50; // Debounce delay for batching events

/**
 * Flush all pending changes to the store in a single state update.
 * This prevents render cascades from rapid WebSocket events.
 */
const flushPendingChanges = () => {
  flushTimeoutId = null;
  const changes = pendingChanges;
  pendingChanges = createEmptyPendingChanges();

  // Check if there are any changes to apply
  const hasChanges =
    changes.seeds.inserts.length > 0 ||
    changes.seeds.updates.size > 0 ||
    changes.seeds.deletes.size > 0 ||
    changes.outputs.inserts.length > 0 ||
    changes.outputs.updates.size > 0 ||
    changes.outputs.deletes.size > 0 ||
    changes.outputs.deletesBySeedId.size > 0 ||
    changes.groups.inserts.length > 0 ||
    changes.groups.updates.size > 0 ||
    changes.groups.deletes.size > 0 ||
    changes.notes.inserts.length > 0 ||
    changes.notes.updates.size > 0 ||
    changes.notes.deletes.size > 0 ||
    changes.events.inserts.length > 0 ||
    changes.events.updates.size > 0 ||
    changes.events.deletes.size > 0;

  if (!hasChanges) {
    return;
  }

  logger.debug('realtime', 'Flushing batched changes', {
    seedInserts: changes.seeds.inserts.length,
    seedUpdates: changes.seeds.updates.size,
    seedDeletes: changes.seeds.deletes.size,
    outputInserts: changes.outputs.inserts.length,
    outputUpdates: changes.outputs.updates.size,
    outputDeletes: changes.outputs.deletes.size,
  });

  // Apply all changes in a single state update
  useContentStore.setState((state) => {
    let newSeeds = state.seeds;
    let newOutputs = state.outputs;
    let newGroups = state.groups;
    let newNotes = state.notes;
    let newEvents = state.events;

    // Process seeds
    if (changes.seeds.deletes.size > 0) {
      newSeeds = newSeeds.filter((s) => !changes.seeds.deletes.has(s.id));
    }
    if (changes.seeds.updates.size > 0) {
      newSeeds = newSeeds.map((s) => {
        const update = changes.seeds.updates.get(s.id);
        return update ? { ...s, ...update } : s;
      });
    }
    if (changes.seeds.inserts.length > 0) {
      const existingIds = new Set(newSeeds.map((s) => s.id));
      const newInserts = changes.seeds.inserts.filter((s) => !existingIds.has(s.id));
      if (newInserts.length > 0) {
        newSeeds = [...newSeeds, ...newInserts];
      }
    }

    // Process outputs
    if (changes.outputs.deletes.size > 0 || changes.outputs.deletesBySeedId.size > 0) {
      newOutputs = newOutputs.filter(
        (o) => !changes.outputs.deletes.has(o.id) && !changes.outputs.deletesBySeedId.has(o.seedId)
      );
    }
    if (changes.outputs.updates.size > 0) {
      newOutputs = newOutputs.map((o) => {
        const update = changes.outputs.updates.get(o.id);
        return update ? { ...o, ...update } : o;
      });
    }
    if (changes.outputs.inserts.length > 0) {
      const existingIds = new Set(newOutputs.map((o) => o.id));
      const newInserts = changes.outputs.inserts.filter((o) => !existingIds.has(o.id));
      if (newInserts.length > 0) {
        newOutputs = [...newOutputs, ...newInserts];
      }
    }

    // Process groups
    if (changes.groups.deletes.size > 0) {
      newGroups = newGroups.filter((g) => !changes.groups.deletes.has(g.id));
    }
    if (changes.groups.updates.size > 0) {
      newGroups = newGroups.map((g) => {
        const update = changes.groups.updates.get(g.id);
        return update ? { ...g, ...update } : g;
      });
    }
    if (changes.groups.inserts.length > 0) {
      const existingIds = new Set(newGroups.map((g) => g.id));
      const newInserts = changes.groups.inserts.filter((g) => !existingIds.has(g.id));
      if (newInserts.length > 0) {
        newGroups = [...newGroups, ...newInserts];
      }
    }

    // Process notes
    if (changes.notes.deletes.size > 0) {
      newNotes = newNotes.filter((n) => !changes.notes.deletes.has(n.id));
    }
    if (changes.notes.updates.size > 0) {
      newNotes = newNotes.map((n) => {
        const update = changes.notes.updates.get(n.id);
        return update ? { ...n, ...update } : n;
      });
    }
    if (changes.notes.inserts.length > 0) {
      const existingIds = new Set(newNotes.map((n) => n.id));
      const newInserts = changes.notes.inserts.filter((n) => !existingIds.has(n.id));
      if (newInserts.length > 0) {
        newNotes = [...newNotes, ...newInserts];
      }
    }

    // Process events
    if (changes.events.deletes.size > 0) {
      newEvents = newEvents.filter((e) => !changes.events.deletes.has(e.id));
    }
    if (changes.events.updates.size > 0) {
      newEvents = newEvents.map((e) => {
        const update = changes.events.updates.get(e.id);
        return update ? { ...e, ...update } : e;
      });
    }
    if (changes.events.inserts.length > 0) {
      const existingIds = new Set(newEvents.map((e) => e.id));
      const newInserts = changes.events.inserts.filter((e) => !existingIds.has(e.id));
      if (newInserts.length > 0) {
        newEvents = [...newEvents, ...newInserts];
      }
    }

    return {
      seeds: newSeeds,
      outputs: newOutputs,
      groups: newGroups,
      notes: newNotes,
      events: newEvents,
    };
  });
};

/**
 * Schedule a flush of pending changes (debounced)
 */
const scheduleFlush = () => {
  if (flushTimeoutId === null) {
    flushTimeoutId = setTimeout(flushPendingChanges, BATCH_DELAY_MS);
  }
};

// =============================================================================
// STORE
// =============================================================================

export const useContentStore = create<ContentState>()((set, get) => ({
  // Initial state
  seeds: [],
  outputs: [],
  groups: [],
  notes: [],
  events: [],
  loading: false,
  initialLoadComplete: false,
  error: null,
  filters: {},

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  fetchAllData: async () => {
    set({ loading: true, error: null });

    try {
      console.log('[contentStore] Fetching all data including notes and Elevate events...');
      const [seedsResult, groupsResult, eventsResult, notesResult, elevateResult] = await Promise.all([
        seedsService.fetchAll(true),
        groupsService.fetchAll(),
        eventsService.fetchAll(),
        notesService.fetchAll(),
        elevateService.fetchEvents(),
      ]);

      console.log('[contentStore] Notes fetch result:', {
        data: notesResult.data,
        error: notesResult.error,
        count: notesResult.data?.length,
      });

      if (seedsResult.error) {
        throw seedsResult.error;
      }

      if (groupsResult.error) {
        throw groupsResult.error;
      }

      // Note: We don't throw on events/notes/elevate error to maintain backwards compatibility
      // These APIs might not exist yet in some deployments

      // Extract outputs from seeds response
      const seeds = seedsResult.data || [];
      const outputs: ContentOutput[] = [];

      seeds.forEach((seed) => {
        if (seed.outputs) {
          outputs.push(...seed.outputs);
        }
      });

      // Merge local events with Elevate events (Elevate events are read-only external events)
      const localEvents = eventsResult.data || [];
      const elevateEvents = elevateResult.data || [];
      const allEvents = [...localEvents, ...elevateEvents];

      set({
        seeds,
        outputs,
        groups: groupsResult.data || [],
        events: allEvents,
        notes: notesResult.data || [],
        loading: false,
        initialLoadComplete: true,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load data';
      set({ loading: false, error: message });
      showToast('error', message);
    }
  },

  fetchSeeds: async () => {
    const { data, error } = await seedsService.fetchAll(true);

    if (error) {
      showToast('error', `Failed to fetch seeds: ${error.message}`);
      return;
    }

    if (data) {
      // Extract outputs from seeds
      const outputs: ContentOutput[] = [];
      data.forEach((seed) => {
        if (seed.outputs) {
          outputs.push(...seed.outputs);
        }
      });

      set({ seeds: data, outputs });
    }
  },

  fetchOutputs: async () => {
    const { data, error } = await outputsService.fetchAll();

    if (error) {
      showToast('error', `Failed to fetch outputs: ${error.message}`);
      return;
    }

    if (data) {
      set({ outputs: data });
    }
  },

  fetchGroups: async () => {
    const { data, error } = await groupsService.fetchAll();

    if (error) {
      showToast('error', `Failed to fetch groups: ${error.message}`);
      return;
    }

    if (data) {
      set({ groups: data });
    }
  },

  fetchEvents: async () => {
    const { data, error } = await eventsService.fetchAll();

    if (error) {
      showToast('error', `Failed to fetch events: ${error.message}`);
      return;
    }

    if (data) {
      set({ events: data });
    }
  },

  // ==========================================================================
  // SEEDS ACTIONS
  // ==========================================================================

  setSeeds: (seeds) => set({ seeds }),

  addSeed: async (data) => {
    set({ loading: true });

    const seedData: Partial<ContentSeed> = {
      title: data.title,
      description: data.description || null,
      status: data.status,
      priority: data.priority,
      silo: data.silo || null,
      targetDate: data.targetDate || null,
      tags: data.tags || [],
      groupId: data.groupId || null,
      order: get().seeds.length,
    };

    const { data: newSeed, error } = await seedsService.create(seedData);

    set({ loading: false });

    if (error) {
      showToast('error', `Failed to create seed: ${error.message}`);
      return null;
    }

    if (newSeed) {
      set((state) => ({
        seeds: [...state.seeds, newSeed],
      }));
      showToast('success', 'Seed created successfully');
      return newSeed;
    }

    return null;
  },

  updateSeed: async (id, updates) => {
    // Optimistic update
    const originalSeeds = get().seeds;
    set((state) => ({
      seeds: state.seeds.map((seed) =>
        seed.id === id
          ? { ...seed, ...updates, updatedAt: new Date().toISOString() }
          : seed
      ),
    }));

    const { error } = await seedsService.update(id, updates);

    if (error) {
      // Rollback on error
      set({ seeds: originalSeeds });
      showToast('error', `Failed to update seed: ${error.message}`);
      return false;
    }

    showToast('success', 'Seed updated');
    return true;
  },

  deleteSeed: async (id) => {
    // Optimistic update
    const originalSeeds = get().seeds;
    const originalOutputs = get().outputs;
    const originalNotes = get().notes;

    set((state) => ({
      seeds: state.seeds.filter((seed) => seed.id !== id),
      outputs: state.outputs.filter((output) => output.seedId !== id),
      notes: state.notes.filter((note) => note.seedId !== id),
    }));

    const { error } = await seedsService.delete(id);

    if (error) {
      // Rollback on error
      set({
        seeds: originalSeeds,
        outputs: originalOutputs,
        notes: originalNotes,
      });
      showToast('error', `Failed to delete seed: ${error.message}`);
      return false;
    }

    showToast('success', 'Seed deleted');
    return true;
  },

  updateSeedStatus: async (id, status) => {
    return get().updateSeed(id, { status });
  },

  reorderSeeds: (sourceIndex, destIndex, groupId) => {
    set((state) => {
      const groupSeeds = groupId
        ? state.seeds.filter((s) => s.groupId === groupId)
        : state.seeds.filter((s) => !s.groupId);

      const [removed] = groupSeeds.splice(sourceIndex, 1);
      groupSeeds.splice(destIndex, 0, removed);

      const updatedGroupSeeds = groupSeeds.map((seed, index) => ({
        ...seed,
        order: index,
      }));

      const otherSeeds = groupId
        ? state.seeds.filter((s) => s.groupId !== groupId)
        : state.seeds.filter((s) => s.groupId !== null);

      return { seeds: [...otherSeeds, ...updatedGroupSeeds] };
    });

    // Sync to backend (fire and forget)
    const groupSeeds = groupId
      ? get().seeds.filter((s) => s.groupId === groupId)
      : get().seeds.filter((s) => !s.groupId);

    const orders = groupSeeds.map((seed, index) => ({ id: seed.id, order: index }));
    seedsService.reorder(orders).catch((error) => {
      console.error('Failed to sync seed order:', error);
    });
  },

  moveSeedToGroup: (seedId, targetGroupId, targetIndex) => {
    const seed = get().seeds.find((s) => s.id === seedId);
    if (!seed) return;

    const sourceGroupId = seed.groupId;

    set((state) => {
      // Get seeds in the target group
      const targetGroupSeeds = targetGroupId
        ? state.seeds.filter((s) => s.groupId === targetGroupId && s.id !== seedId)
        : state.seeds.filter((s) => !s.groupId && s.id !== seedId);

      // Insert seed at target position
      targetGroupSeeds.splice(targetIndex, 0, { ...seed, groupId: targetGroupId });

      // Update order for target group seeds
      const updatedTargetSeeds = targetGroupSeeds.map((s, index) => ({
        ...s,
        order: index,
      }));

      // Get all other seeds (not in target group and not the moved seed)
      const otherSeeds = state.seeds.filter(
        (s) => s.id !== seedId && (targetGroupId ? s.groupId !== targetGroupId : s.groupId !== null)
      );

      // If moving from a different group, reorder the source group
      let updatedSourceSeeds: ContentSeed[] = [];
      if (sourceGroupId !== targetGroupId) {
        const sourceGroupSeeds = sourceGroupId
          ? otherSeeds.filter((s) => s.groupId === sourceGroupId)
          : otherSeeds.filter((s) => !s.groupId);
        updatedSourceSeeds = sourceGroupSeeds.map((s, index) => ({ ...s, order: index }));
      }

      // Combine all seeds
      const remainingSeeds = otherSeeds.filter((s) =>
        sourceGroupId !== targetGroupId
          ? sourceGroupId
            ? s.groupId !== sourceGroupId
            : s.groupId !== null
          : true
      );

      return {
        seeds: [...remainingSeeds, ...updatedSourceSeeds, ...updatedTargetSeeds],
      };
    });

    // Sync the moved seed's groupId to backend
    seedsService.update(seedId, { groupId: targetGroupId, order: targetIndex }).catch((error) => {
      console.error('Failed to sync seed move:', error);
    });

    // Reorder target group
    const targetGroupSeeds = targetGroupId
      ? get().seeds.filter((s) => s.groupId === targetGroupId)
      : get().seeds.filter((s) => !s.groupId);
    const targetOrders = targetGroupSeeds.map((s, index) => ({ id: s.id, order: index }));
    seedsService.reorder(targetOrders).catch((error) => {
      console.error('Failed to sync target group order:', error);
    });

    // Reorder source group if different
    if (sourceGroupId !== targetGroupId) {
      const sourceGroupSeeds = sourceGroupId
        ? get().seeds.filter((s) => s.groupId === sourceGroupId)
        : get().seeds.filter((s) => !s.groupId);
      const sourceOrders = sourceGroupSeeds.map((s, index) => ({ id: s.id, order: index }));
      seedsService.reorder(sourceOrders).catch((error) => {
        console.error('Failed to sync source group order:', error);
      });
    }
  },

  // ==========================================================================
  // OUTPUTS ACTIONS
  // ==========================================================================

  setOutputs: (outputs) => set({ outputs }),

  addOutput: async (data) => {
    set({ loading: true });

    // Calculate order for new output (append to end of seed's outputs)
    const seedOutputs = get().outputs.filter((o) => o.seedId === data.seedId);
    const maxOrder = seedOutputs.length > 0
      ? Math.max(...seedOutputs.map((o) => o.order ?? 0))
      : -1;

    const outputData: Partial<ContentOutput> = {
      seedId: data.seedId,
      title: data.title,
      contentType: data.contentType,
      platforms: data.platforms || [],
      status: data.status,
      author: data.author || null,
      assignedTo: data.assignedTo || null,
      dueDate: data.dueDate || null,
      publishDate: data.publishDate || null,
      content: data.content || null,
      excerpt: data.excerpt || null,
      focusKeyword: data.focusKeyword || null,
      seoTitle: data.seoTitle || null,
      metaDescription: data.metaDescription || null,
      socialCopy: data.socialCopy || null,
      hashtags: data.hashtags || null,
      featuredImage: data.featuredImage || null,
      assets: data.assets || [],
      order: maxOrder + 1,
    };

    const { data: newOutput, error } = await outputsService.create(outputData);

    set({ loading: false });

    if (error) {
      showToast('error', `Failed to create output: ${error.message}`);
      return null;
    }

    if (newOutput) {
      set((state) => ({
        outputs: [...state.outputs, newOutput],
      }));
      showToast('success', 'Output created successfully');
      return newOutput;
    }

    return null;
  },

  updateOutput: async (id, updates) => {
    // Optimistic update
    const originalOutputs = get().outputs;
    set((state) => ({
      outputs: state.outputs.map((output) =>
        output.id === id
          ? { ...output, ...updates, updatedAt: new Date().toISOString() }
          : output
      ),
    }));

    const { error } = await outputsService.update(id, updates);

    if (error) {
      // Rollback on error
      set({ outputs: originalOutputs });
      showToast('error', `Failed to update output: ${error.message}`);
      return false;
    }

    showToast('success', 'Output updated');
    return true;
  },

  deleteOutput: async (id) => {
    // Optimistic update
    const originalOutputs = get().outputs;
    const originalNotes = get().notes;

    set((state) => ({
      outputs: state.outputs.filter((output) => output.id !== id),
      notes: state.notes.filter((note) => note.outputId !== id),
    }));

    const { error } = await outputsService.delete(id);

    if (error) {
      // Rollback on error
      set({ outputs: originalOutputs, notes: originalNotes });
      showToast('error', `Failed to delete output: ${error.message}`);
      return false;
    }

    showToast('success', 'Output deleted');
    return true;
  },

  updateOutputStatus: async (id, status) => {
    return get().updateOutput(id, { status });
  },

  reorderOutputs: (seedId, sourceIndex, destIndex) => {
    set((state) => {
      // Get outputs for this seed, sorted by current order
      const seedOutputs = [...state.outputs.filter((o) => o.seedId === seedId)]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const otherOutputs = state.outputs.filter((o) => o.seedId !== seedId);

      // Reorder
      const [removed] = seedOutputs.splice(sourceIndex, 1);
      seedOutputs.splice(destIndex, 0, removed);

      // Update order values
      const updatedSeedOutputs = seedOutputs.map((output, index) => ({
        ...output,
        order: index,
      }));

      return { outputs: [...otherOutputs, ...updatedSeedOutputs] };
    });

    // Sync to backend (fire and forget)
    const seedOutputs = get().outputs
      .filter((o) => o.seedId === seedId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const orders = seedOutputs.map((output, index) => ({ id: output.id, order: index }));
    outputsService.reorder(orders).catch((error) => {
      console.error('Failed to sync output order:', error);
    });
  },

  // ==========================================================================
  // GROUPS ACTIONS
  // ==========================================================================

  setGroups: (groups) => set({ groups }),

  addGroup: async (data) => {
    set({ loading: true });

    const groupData: Partial<ContentGroup> = {
      title: data.title,
      backgroundColour: data.backgroundColour,
      textColour: data.textColour,
      order: get().groups.length,
      isCollapsed: false,
    };

    const { data: newGroup, error } = await groupsService.create(groupData);

    set({ loading: false });

    if (error) {
      showToast('error', `Failed to create group: ${error.message}`);
      return null;
    }

    if (newGroup) {
      set((state) => ({
        groups: [...state.groups, newGroup],
      }));
      showToast('success', 'Group created successfully');
      return newGroup;
    }

    return null;
  },

  updateGroup: async (id, updates) => {
    // Optimistic update
    const originalGroups = get().groups;
    set((state) => ({
      groups: state.groups.map((group) =>
        group.id === id
          ? { ...group, ...updates, updatedAt: new Date().toISOString() }
          : group
      ),
    }));

    const { error } = await groupsService.update(id, updates);

    if (error) {
      // Rollback on error
      set({ groups: originalGroups });
      showToast('error', `Failed to update group: ${error.message}`);
      return false;
    }

    return true;
  },

  deleteGroup: async (id) => {
    // Optimistic update
    const originalGroups = get().groups;
    const originalSeeds = get().seeds;

    set((state) => ({
      groups: state.groups.filter((group) => group.id !== id),
      seeds: state.seeds.map((seed) =>
        seed.groupId === id ? { ...seed, groupId: null } : seed
      ),
    }));

    const { error } = await groupsService.delete(id);

    if (error) {
      // Rollback on error
      set({ groups: originalGroups, seeds: originalSeeds });
      showToast('error', `Failed to delete group: ${error.message}`);
      return false;
    }

    showToast('success', 'Group deleted');
    return true;
  },

  toggleGroupCollapsed: (id) => {
    const group = get().groups.find((g) => g.id === id);
    if (group) {
      get().updateGroup(id, { isCollapsed: !group.isCollapsed });
    }
  },

  reorderGroups: (sourceIndex, destIndex) => {
    set((state) => {
      const newGroups = [...state.groups];
      const [removed] = newGroups.splice(sourceIndex, 1);
      newGroups.splice(destIndex, 0, removed);

      return {
        groups: newGroups.map((group, index) => ({ ...group, order: index })),
      };
    });

    // Sync to backend
    const orders = get().groups.map((group, index) => ({ id: group.id, order: index }));
    groupsService.reorder(orders).catch((error) => {
      console.error('Failed to sync group order:', error);
    });
  },

  // ==========================================================================
  // NOTES ACTIONS
  // ==========================================================================

  setNotes: (notes) => set({ notes }),

  addNote: async (data) => {
    const noteData: Partial<ContentNote> = {
      seedId: data.seedId || null,
      outputId: data.outputId || null,
      text: data.text,
      isActionable: data.isActionable,
      isComplete: false,
      mentions: data.mentions || [],
      parentNoteId: data.parentNoteId || null,
    };

    const { data: newNote, error } = await notesService.create(noteData);

    if (error) {
      showToast('error', `Failed to create note: ${error.message}`);
      return null;
    }

    if (newNote) {
      set((state) => ({
        notes: [...state.notes, newNote],
      }));

      // Send notifications for @mentions
      const mentions = data.mentions || [];
      console.log('[contentStore] Note created, checking mentions:', { mentions, noteId: newNote.id });

      if (mentions.length > 0) {
        // Get current user (the person who made the mention)
        const currentUser = useAuthStore.getState().user;
        const mentionerName = getDisplayName(currentUser?.name || 'Someone');
        console.log('[contentStore] Mentioner:', { currentUser, mentionerName });

        // Get content title for the notification
        let contentTitle = '';
        let entityType: 'seed' | 'output' | 'note' = 'note';
        let entityId = newNote.id;

        if (data.outputId) {
          const output = get().outputs.find((o) => o.id === data.outputId);
          contentTitle = output?.title || 'content';
          entityType = 'output';
          entityId = data.outputId;
        } else if (data.seedId) {
          const seed = get().seeds.find((s) => s.id === data.seedId);
          contentTitle = seed?.title || 'content';
          entityType = 'seed';
          entityId = data.seedId;
        }
        console.log('[contentStore] Content context:', { contentTitle, entityType, entityId });

        // Fetch all users to look up mentioned user details
        const { data: allUsers } = await usersService.fetchAll();
        console.log('[contentStore] All users for matching:', allUsers?.map(u => ({ id: u.id, name: u.name })));

        // Build content URL for notifications
        const contentUrl = typeof window !== 'undefined' ? window.location.origin : undefined;

        // Check if Teams is configured
        const teamsConfigured = teamsNotificationHelper.isConfigured();
        console.log('[contentStore] Teams configured:', teamsConfigured);

        // Send notifications for each mentioned user (mentions contains user IDs)
        for (const mentionedUserId of mentions) {
          console.log('[contentStore] Processing mention for userId:', mentionedUserId);
          // Find user by ID
          const mentionedUser = allUsers?.find((u) => u.id === mentionedUserId);
          console.log('[contentStore] Found mentioned user:', mentionedUser);

          // Don't notify yourself
          if (!mentionedUser) {
            console.log('[contentStore] Skipping notification - mentioned user not found for ID:', mentionedUserId);
          } else if (mentionedUser.id === currentUser?.id) {
            console.log('[contentStore] Skipping notification - self-mention detected:', {
              mentionedUserId: mentionedUser.id,
              currentUserId: currentUser?.id,
              userName: getDisplayName(mentionedUser.name)
            });
          } else {
            console.log('[contentStore] Sending notifications for:', {
              mentionedUser: getDisplayName(mentionedUser.name),
              mentionedUserId: mentionedUser.id,
              currentUserId: currentUser?.id,
              teamsConfigured: teamsNotificationHelper.isConfigured()
            });

            // Send in-app notification
            notificationsService.notifyMention(
              mentionedUser.id,
              mentionerName,
              entityType,
              entityId,
              contentTitle
            ).catch((err) => console.error('Failed to send in-app mention notification:', err));

            // Send Teams notification with link to the app
            teamsNotificationHelper.notifyMention(
              getDisplayName(mentionedUser.name),
              mentionerName,
              contentTitle,
              data.text,
              contentUrl
            ).then(() => console.log('[contentStore] Teams notification sent successfully'))
            .catch((err) => console.error('Failed to send Teams mention notification:', err));
          }
        }
      }

      return newNote;
    }

    return null;
  },

  updateNote: async (id, updates) => {
    const originalNotes = get().notes;
    set((state) => ({
      notes: state.notes.map((note) =>
        note.id === id ? { ...note, ...updates } : note
      ),
    }));

    const { error } = await notesService.update(id, updates);

    if (error) {
      set({ notes: originalNotes });
      showToast('error', `Failed to update note: ${error.message}`);
      return false;
    }

    return true;
  },

  deleteNote: async (id) => {
    const originalNotes = get().notes;
    set((state) => ({
      notes: state.notes.filter((note) => note.id !== id),
    }));

    const { error } = await notesService.delete(id);

    if (error) {
      set({ notes: originalNotes });
      showToast('error', `Failed to delete note: ${error.message}`);
      return false;
    }

    return true;
  },

  toggleNoteComplete: async (id) => {
    const note = get().notes.find((n) => n.id === id);
    if (note) {
      return get().updateNote(id, { isComplete: !note.isComplete });
    }
    return false;
  },

  // ==========================================================================
  // EVENTS ACTIONS
  // ==========================================================================

  setEvents: (events) => set({ events }),

  addEvent: async (data) => {
    set({ loading: true });

    const eventData: Partial<ContentEvent> = {
      activityType: data.activityType,
      title: data.title,
      location: data.location || null,
      startDate: data.startDate,
      endDate: data.endDate || null,
      notes: data.notes || null,
      url: data.url || null,
      awardsCategoryUrl: data.awardsCategoryUrl || null,
      entryDeadline: data.entryDeadline || null,
      order: get().events.length,
    };

    const { data: newEvent, error } = await eventsService.create(eventData);

    set({ loading: false });

    if (error) {
      showToast('error', `Failed to create event: ${error.message}`);
      return null;
    }

    if (newEvent) {
      set((state) => ({
        events: [...state.events, newEvent],
      }));
      showToast('success', 'Event created successfully');
      return newEvent;
    }

    return null;
  },

  updateEvent: async (id, updates) => {
    // Optimistic update
    const originalEvents = get().events;
    set((state) => ({
      events: state.events.map((event) =>
        event.id === id
          ? { ...event, ...updates, updatedAt: new Date().toISOString() }
          : event
      ),
    }));

    const { error } = await eventsService.update(id, updates);

    if (error) {
      // Rollback on error
      set({ events: originalEvents });
      showToast('error', `Failed to update event: ${error.message}`);
      return false;
    }

    showToast('success', 'Event updated');
    return true;
  },

  deleteEvent: async (id) => {
    // Optimistic update
    const originalEvents = get().events;

    set((state) => ({
      events: state.events.filter((event) => event.id !== id),
    }));

    const { error } = await eventsService.delete(id);

    if (error) {
      // Rollback on error
      set({ events: originalEvents });
      showToast('error', `Failed to delete event: ${error.message}`);
      return false;
    }

    showToast('success', 'Event deleted');
    return true;
  },

  reorderEvents: (sourceIndex, destIndex) => {
    set((state) => {
      const newEvents = [...state.events];
      const [removed] = newEvents.splice(sourceIndex, 1);
      newEvents.splice(destIndex, 0, removed);

      return {
        events: newEvents.map((event, index) => ({ ...event, order: index })),
      };
    });

    // Sync to backend
    const orders = get().events.map((event, index) => ({ id: event.id, order: index }));
    eventsService.reorder(orders).catch((error) => {
      console.error('Failed to sync event order:', error);
    });
  },

  // ==========================================================================
  // FILTER ACTIONS
  // ==========================================================================

  setFilters: (filters) => set({ filters }),

  clearFilters: () => set({ filters: {} }),

  // ==========================================================================
  // LOADING/ERROR ACTIONS
  // ==========================================================================

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // ==========================================================================
  // BULK ACTIONS
  // ==========================================================================

  bulkUpdateStatus: async (ids, type, status) => {
    if (type === 'seed') {
      // Optimistic update
      const originalSeeds = get().seeds;
      set((state) => ({
        seeds: state.seeds.map((seed) =>
          ids.includes(seed.id)
            ? { ...seed, status, updatedAt: new Date().toISOString() }
            : seed
        ),
      }));

      const { error } = await seedsService.bulkUpdateStatus(ids, status);

      if (error) {
        set({ seeds: originalSeeds });
        showToast('error', `Failed to update seeds: ${error.message}`);
        return false;
      }
    } else {
      // Optimistic update
      const originalOutputs = get().outputs;
      set((state) => ({
        outputs: state.outputs.map((output) =>
          ids.includes(output.id)
            ? { ...output, status, updatedAt: new Date().toISOString() }
            : output
        ),
      }));

      const { error } = await outputsService.bulkUpdateStatus(ids, status);

      if (error) {
        set({ outputs: originalOutputs });
        showToast('error', `Failed to update outputs: ${error.message}`);
        return false;
      }
    }

    showToast('success', `Updated ${ids.length} items`);
    return true;
  },

  bulkDelete: async (seedIds, outputIds) => {
    const originalSeeds = get().seeds;
    const originalOutputs = get().outputs;
    const originalNotes = get().notes;

    // Optimistic update
    set((state) => ({
      seeds: state.seeds.filter((seed) => !seedIds.includes(seed.id)),
      outputs: state.outputs.filter(
        (output) =>
          !outputIds.includes(output.id) && !seedIds.includes(output.seedId)
      ),
      notes: state.notes.filter(
        (note) =>
          (!note.seedId || !seedIds.includes(note.seedId)) &&
          (!note.outputId || !outputIds.includes(note.outputId))
      ),
    }));

    const errors: string[] = [];

    if (seedIds.length > 0) {
      const { error } = await seedsService.bulkDelete(seedIds);
      if (error) errors.push(error.message);
    }

    if (outputIds.length > 0) {
      const { error } = await outputsService.bulkDelete(outputIds);
      if (error) errors.push(error.message);
    }

    if (errors.length > 0) {
      set({
        seeds: originalSeeds,
        outputs: originalOutputs,
        notes: originalNotes,
      });
      showToast('error', `Failed to delete: ${errors.join(', ')}`);
      return false;
    }

    showToast('success', `Deleted ${seedIds.length + outputIds.length} items`);
    return true;
  },

  // ==========================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ==========================================================================

  subscribeToChanges: () => {
    const handleChange = get().handleContentChange;

    // Subscribe to all content changes
    const subscription = eventsClient.subscribe('all', handleChange);

    return () => {
      subscription.unsubscribe();
    };
  },

  handleContentChange: (event: ContentChangeEvent) => {
    const { eventType, table, recordId, data } = event;

    // Buffer events for batched processing instead of updating state immediately
    // This prevents browser freezes when many WebSocket events arrive rapidly
    switch (table) {
      case 'seeds':
        if (eventType === 'INSERT' && data) {
          pendingChanges.seeds.inserts.push(data as ContentSeed);
        } else if (eventType === 'UPDATE' && data) {
          pendingChanges.seeds.updates.set(recordId, data as Partial<ContentSeed>);
        } else if (eventType === 'DELETE') {
          pendingChanges.seeds.deletes.add(recordId);
          // Also delete outputs associated with this seed
          pendingChanges.outputs.deletesBySeedId.add(recordId);
        }
        break;

      case 'outputs':
        if (eventType === 'INSERT' && data) {
          pendingChanges.outputs.inserts.push(data as ContentOutput);
        } else if (eventType === 'UPDATE' && data) {
          pendingChanges.outputs.updates.set(recordId, data as Partial<ContentOutput>);
        } else if (eventType === 'DELETE') {
          pendingChanges.outputs.deletes.add(recordId);
        }
        break;

      case 'groups':
        if (eventType === 'INSERT' && data) {
          pendingChanges.groups.inserts.push(data as ContentGroup);
        } else if (eventType === 'UPDATE' && data) {
          pendingChanges.groups.updates.set(recordId, data as Partial<ContentGroup>);
        } else if (eventType === 'DELETE') {
          pendingChanges.groups.deletes.add(recordId);
        }
        break;

      case 'notes':
        if (eventType === 'INSERT' && data) {
          pendingChanges.notes.inserts.push(data as ContentNote);
        } else if (eventType === 'UPDATE' && data) {
          pendingChanges.notes.updates.set(recordId, data as Partial<ContentNote>);
        } else if (eventType === 'DELETE') {
          pendingChanges.notes.deletes.add(recordId);
        }
        break;

      case 'events':
        if (eventType === 'INSERT' && data) {
          pendingChanges.events.inserts.push(data as ContentEvent);
        } else if (eventType === 'UPDATE' && data) {
          pendingChanges.events.updates.set(recordId, data as Partial<ContentEvent>);
        } else if (eventType === 'DELETE') {
          pendingChanges.events.deletes.add(recordId);
        }
        break;
    }

    // Schedule a batched flush (debounced)
    scheduleFlush();
  },
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectSeeds = (state: ContentState) => state.seeds;
export const selectOutputs = (state: ContentState) => state.outputs;
export const selectGroups = (state: ContentState) => state.groups;
export const selectNotes = (state: ContentState) => state.notes;
export const selectEvents = (state: ContentState) => state.events;
export const selectLoading = (state: ContentState) => state.loading;
export const selectInitialLoadComplete = (state: ContentState) => state.initialLoadComplete;
export const selectError = (state: ContentState) => state.error;
export const selectFilters = (state: ContentState) => state.filters;

// Get seed by ID
export const selectSeedById = (id: string) => (state: ContentState) =>
  state.seeds.find((seed) => seed.id === id);

// Get output by ID
export const selectOutputById = (id: string) => (state: ContentState) =>
  state.outputs.find((output) => output.id === id);

// Get outputs for a specific seed
export const selectOutputsBySeedId = (seedId: string) => (state: ContentState) =>
  state.outputs.filter((output) => output.seedId === seedId);

// Get seeds by group
export const selectSeedsByGroupId = (groupId: string | null) => (state: ContentState) =>
  state.seeds.filter((seed) => seed.groupId === groupId);

// Get notes for a seed
export const selectNotesBySeedId = (seedId: string) => (state: ContentState) =>
  state.notes.filter((note) => note.seedId === seedId);

// Get notes for an output
export const selectNotesByOutputId = (outputId: string) => (state: ContentState) =>
  state.notes.filter((note) => note.outputId === outputId);

// Get event by ID
export const selectEventById = (id: string) => (state: ContentState) =>
  state.events.find((event) => event.id === id);

// Get seeds with their outputs
export const selectSeedsWithOutputs = (state: ContentState) =>
  state.seeds.map((seed) => ({
    ...seed,
    outputs: state.outputs.filter((output) => output.seedId === seed.id),
  }));

// Get content by status
export const selectContentByStatus = (status: ContentStatus) => (state: ContentState) => ({
  seeds: state.seeds.filter((seed) => seed.status === status),
  outputs: state.outputs.filter((output) => output.status === status),
});

// Get statistics
export const selectStats = (state: ContentState) => ({
  totalSeeds: state.seeds.length,
  totalOutputs: state.outputs.length,
  byStatus: Object.fromEntries(
    ['Idea', 'Scheduled', 'In Progress', 'Review', 'Approved', 'Published', 'Archived'].map(
      (status) => [
        status,
        state.seeds.filter((s) => s.status === status).length +
          state.outputs.filter((o) => o.status === status).length,
      ]
    )
  ),
});

export default useContentStore;
